import type { ReactNode } from "react";
import { Eyebrow, HandNote } from "@appcaire/ui";

export interface BackdropHeroProps {
  id: string;
  eyebrow: string;
  title: ReactNode;
  description: ReactNode;
  actions?: ReactNode;
  /** A current, full product screen; it fills the hero behind the copy. */
  image: { src: string; width: number; height: number; alt?: string };
  /** Short handwritten margin note on the screen (website only, ~8 words). */
  note?: string;
  /** Optional line under the actions, for example a trust link. */
  footer?: ReactNode;
  /** `compact` for long titles (a help article's full name). */
  titleSize?: "display" | "compact";
}

/**
 * Brand 2.2 page hero: a real product screen fills the background, slightly
 * tilted, faded under the copy so the text stays readable, with one
 * handwritten note. The screen is decorative here; the page explains it.
 */
export function BackdropHero({
  id,
  eyebrow,
  title,
  description,
  actions,
  image,
  note,
  footer,
  titleSize = "display",
}: BackdropHeroProps) {
  return (
    <section
      aria-labelledby={id}
      className="relative isolate overflow-hidden bg-caire-page"
    >
      <div
        aria-hidden={image.alt ? undefined : true}
        className="pointer-events-none absolute inset-0 -z-10"
      >
        <img
          src={image.src}
          width={image.width}
          height={image.height}
          alt={image.alt ?? ""}
          fetchPriority="high"
          decoding="async"
          className="absolute left-[18%] top-[22%] w-[150%] max-w-none -rotate-[4deg] rounded-[28px] shadow-window ring-1 ring-caire-hairline sm:left-[30%] sm:w-[110%] lg:left-[40%] lg:top-[10%] lg:w-[82%]"
        />
        <div className="absolute inset-0 bg-[linear-gradient(180deg,hsl(var(--cb-page-hsl))_0%,hsl(var(--cb-page-hsl)/0.96)_45%,hsl(var(--cb-page-hsl)/0.55)_100%)] lg:bg-[linear-gradient(90deg,hsl(var(--cb-page-hsl))_0%,hsl(var(--cb-page-hsl)/0.97)_36%,hsl(var(--cb-page-hsl)/0.45)_62%,hsl(var(--cb-page-hsl)/0)_85%)]" />
      </div>
      {note ? (
        <div className="pointer-events-none absolute bottom-[12%] right-[6%] hidden lg:block">
          <HandNote tilt={-3} size="lg" decorative>
            {note}
          </HandNote>
        </div>
      ) : null}
      <div className="mx-auto flex min-h-[620px] max-w-[1280px] flex-col justify-center px-6 py-20 lg:min-h-[680px] lg:px-8">
        <div className="flex max-w-[620px] flex-col gap-5">
          <Eyebrow>{eyebrow}</Eyebrow>
          <h1
            id={id}
            className={
              titleSize === "compact"
                ? "text-balance text-[2.1rem] font-extrabold leading-[1.08] tracking-[-0.03em] text-caire-ink sm:text-[2.6rem] lg:text-[3rem]"
                : "text-balance text-[2.6rem] font-extrabold leading-[1.02] tracking-[-0.035em] text-caire-ink sm:text-[3.4rem] lg:text-[4rem]"
            }
          >
            {title}
          </h1>
          <p className="max-w-[56ch] text-lg leading-relaxed text-caire-ink/85">
            {description}
          </p>
          {actions ? (
            <div className="mt-2 flex flex-wrap items-center gap-3">
              {actions}
            </div>
          ) : null}
          {footer ? (
            <div className="text-sm text-caire-ink/80">{footer}</div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
